"use client";
import {
  Calendar,
  User,
  Award,
  Star,
  TrendingUp,
  Trash2,
  MessageCircle,
  Search,
  ChevronDown,
  Filter,
  X,
  Info,
} from "lucide-react";
import Modal from "../layout/Modal";
import "./NominationsPage.css";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import BackButton from "../shared/backButton";
import { handleApiError as baseHandleApiError } from "../shared/util";
import SubscriptionExpiredModal from "../dashboard/SubscriptionExpiredModal";
import Pagination from "../shared/paginator/paginator";
import CustomTooltip from "../shared/CustomTooltip";
import * as XLSX from "xlsx";
import CustomReadinessFilter from "../shared/CustomReadinessFilter";
import { getLeadershipPotentialAccess } from "../shared/access.js";

import {
  getLeadershipNominationsApi,
  deleteLeadershipNominationApi,
} from "../../services/peopleService";
import { useTranslation } from "react-i18next";

const NominationsPage = ({ onRemove }) => {
  const { t } = useTranslation();
  const [nominations, setNominations] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [peopleLength, setPeopleLength] = useState(null);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const totalPages = Math.ceil(peopleLength / itemsPerPage);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState({
    show: false,
    type: "",
    title: "",
    message: "",
    userId: null,
  });
  const seniorities = [
    { value: "All", label: t("common.all") },
    {
      value: "Junior",
      label: t("nominationsPage.filters.seniorityOptions.junior"),
    },
    {
      value: "Mid Level",
      label: t("nominationsPage.filters.seniorityOptions.midLevel"),
    },
    {
      value: "Senior",
      label: t("nominationsPage.filters.seniorityOptions.senior"),
    },
    {
      value: "Lead",
      label: t("nominationsPage.filters.seniorityOptions.lead"),
    },
    {
      value: "Manager",
      label: t("nominationsPage.filters.seniorityOptions.manager"),
    },
    {
      value: "Director",
      label: t("nominationsPage.filters.seniorityOptions.director"),
    },
  ];
  const readinessOptions = [
    "All",
    "Promotion-ready",
    "Leadership-ready",
    "Near-ready",
    "Not-ready",
  ];
  const sortOptions = [
    {
      value: "Leadership Score",
      label: t("leadershipPage.filters.leadership"),
    },
    { value: "Name", label: t("common.name") },
  ];
  const [showSearchBar, setShowSearchBar] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [showFiltersPanel, setShowFiltersPanel] = useState(false);
  const [filterSeniority, setFilterSeniority] = useState("All");
  const [sortBy, setSortBy] = useState("Leadership Score");
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInputValue, setSearchInputValue] = useState("");
  const [filterTeam, setFilterTeam] = useState("All");
  const readinessFromUrl = searchParams.getAll("readiness");
  const paramUserIds = searchParams.getAll("userId");
  const leadershipAccess = getLeadershipPotentialAccess();

  const normalizeList = (arr) => {
    const cleaned = Array.from(
      new Set((arr || []).map((x) => (x || "").trim()).filter(Boolean)),
    );
    const hasReal = cleaned.some((x) => x !== "All");
    return hasReal ? cleaned.filter((x) => x !== "All") : [];
  };
  const sanitizeSearch = (val) =>
    // allow letters, numbers, space, dash, underscore, and dot; strip everything else (incl. backslash)
    val.replace(/[\\`~!@#$%^&*()+={}[\]|:;"'<>,.?/]/g, "");
  const getInitialReadiness = () => {
    // 1) if readiness is provided in URL, use it
    if (readinessFromUrl.length) return normalizeList(readinessFromUrl);

    // 2) keep your old behavior for userId deep links (optional)
    if (paramUserIds.length) return ["Promotion-ready", "Leadership-ready"];

    // 3) default = All (empty array means no filter in your code)
    return [];
  };
  const [filterReadiness, setFilterReadiness] = useState(getInitialReadiness);
  useEffect(() => {
    setFilterReadiness(getInitialReadiness());
    setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.toString()]);
  const getNominationsRequest = (overrides = {}) => ({
    pageSize: itemsPerPage,
    pageNo: currentPage,
    searchFilter: searchTerm || undefined,
    level: filterSeniority !== "All" ? filterSeniority : undefined,
    sortBy: sortBy || undefined,
    type:
      Array.isArray(filterReadiness) &&
      filterReadiness.length > 0 &&
      !filterReadiness.includes("All")
        ? filterReadiness
        : undefined,
    ...overrides,
  });

  useEffect(() => {
    const fetchLeadershipNominations = async () => {
      try {
        setLoading(true);
        const response = await getLeadershipNominationsApi(
          getNominationsRequest(),
        );

        const nominationData = Array.isArray(response?.data)
          ? response.data
          : [];
        const nextTotalCount = response?.totalCount ?? nominationData.length;

        setNominations(nominationData);
        setTotalCount(nextTotalCount);
        setPeopleLength(nextTotalCount);
      } catch (error) {
        handleApiError(error, {
          showModal: true,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchLeadershipNominations();
  }, [
    currentPage,
    searchTerm,
    filterTeam,
    filterSeniority,
    filterReadiness,
    sortBy,
    itemsPerPage,
  ]);

  const getAppliedFiltersCount = () => {
    let count = 0;
    if (searchTerm) count++;
    if (filterTeam && filterTeam !== "All") count++;
    if (filterSeniority && filterSeniority !== "All") count++;
    if (
      filterReadiness &&
      filterReadiness.length > 0 &&
      !filterReadiness.includes("All")
    )
      count++;
    if (sortBy && sortBy !== "Leadership Score") count++;
    return count;
  };
  const removeCountFromUrl = () => {
    if (!searchParams.has("count")) return;

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("count");
    setSearchParams(nextParams, { replace: true });
  };
  const addNomination = (nomination) => {
    setNominations((prev) => [
      ...prev,
      { ...nomination, id: Date.now(), nominatedDate: new Date() },
    ]);
  };
  const clearAllFilters = () => {
    removeCountFromUrl();
    setSearchTerm("");
    setFilterTeam("All");
    setFilterSeniority("All");
    setFilterReadiness([]);
    setSortBy("Leadership Score");
    setShowSearchBar(false);
    setShowFiltersPanel(false);
    setCurrentPage(1);
  };
  const [expandedNominators, setExpandedNominators] = useState({});

  const [showNominatorList, setShowNominatorList] = useState({
    show: false,
    title: "",
    nominators: [],
    rating: 0,
  });

  const navigate = useNavigate();

  const downloadNominationsExcel = async () => {
    try {
      const response = await getLeadershipNominationsApi(
        getNominationsRequest({
          pageNo: 1,
          pageSize: totalCount || itemsPerPage,
        }),
      );
      const data = Array.isArray(response?.data) ? response.data : [];

      console.log("Nominations data:", data);
      const headers = [
        t("nominationsPage.excel.headers.name"),
        t("nominationsPage.excel.headers.userId"),
        t("nominationsPage.excel.headers.nominationCount"),
        t("nominationsPage.excel.headers.department"),
        t("nominationsPage.excel.headers.designation"),
        t("nominationsPage.excel.headers.leadershipPotentialScore"),
        t("nominationsPage.excel.headers.leadershipStrengths"),
        t("nominationsPage.excel.headers.rating"),
      ];

      const rows = data.map((it) => ({
        [t("nominationsPage.excel.headers.name")]: it?.name ?? "",
        [t("nominationsPage.excel.headers.userId")]: it?._id ?? "",
        [t("nominationsPage.excel.headers.nominationCount")]:
          it?.nominationCount ?? "",
        [t("nominationsPage.excel.headers.department")]: it?.department ?? "",
        [t("nominationsPage.excel.headers.designation")]: it?.designation ?? "",
        [t("nominationsPage.excel.headers.leadershipPotentialScore")]:
          it?.leadershipPotentialScore ?? "",
        [t("nominationsPage.excel.headers.leadershipStrengths")]: Array.isArray(
          it?.leadershipStrengths,
        )
          ? it.leadershipStrengths.join(", ")
          : (it?.leadershipStrengths ?? ""),
        [t("nominationsPage.excel.headers.rating")]: it?.rating ?? "",
      }));

      const ws = XLSX.utils.json_to_sheet(rows, { header: headers });
      ws["!cols"] = headers.map((h) => ({
        wch: Math.max(14, String(h).length + 2),
      }));

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(
        wb,
        ws,
        t("nominationsPage.excel.sheetName"),
      );

      XLSX.writeFile(
        wb,
        `${t("nominationsPage.excel.filePrefix")}_${new Date()
          .toISOString()
          .slice(0, 10)}.xlsx`,
      );
    } catch (error) {
      console.error("Excel export failed:", error);
      alert(t("nominationsPage.messages.downloadExcelFailed"));
    }
  };
  const handleSelect = (selectedList, selectedItem) => {
    removeCountFromUrl();

    if (selectedItem === "All") {
      setFilterReadiness(["All"]);
      return;
    }
    const keys = selectedList.filter((k) => k !== "All");
    setFilterReadiness(keys);
  };
  const handleReadinessRemove = (selectedList) => {
    removeCountFromUrl();
    const keys = selectedList.filter((k) => k !== "All");
    setFilterReadiness(keys.length ? keys : ["All"]);
  };
  const [showSubscriptionExpired, setShowSubscriptionExpired] = useState(false);
  const [subscriptionMessage, setSubscriptionMessage] = useState("");

  const LeadershipreadinessTooltip = (option) => {
    if (option === "Promotion-ready")
      return t("leadershipPage.tooltips.filterTooltip.promotionReady");
    if (option === "Leadership-ready")
      return t("leadershipPage.tooltips.filterTooltip.leadershipReady");
    if (option === "Near-ready")
      return t("leadershipPage.tooltips.filterTooltip.nearReady");
    if (option === "Not-ready")
      return t("leadershipPage.tooltips.filterTooltip.notReady");
    return "";
  };
  const handleSubscriptionRedirect = () =>
    new Promise((resolve) => {
      setTimeout(() => {
        navigate("/pricing");
        resolve();
      }, 100);
    });

  const handleApiError = (error, options) => {
    if (error?.response?.status === 403) {
      setSubscriptionMessage(
        error?.response?.data?.message || t("subscription.upgradePlanMessage"),
      );
      setShowSubscriptionExpired(true);
      return;
    }
    return baseHandleApiError(error, options);
  };

  const handleSearchButtonClick = () => {
    setCurrentPage(1);
    setSearchTerm(searchInputValue.trim());
    setShowSearchBar(false);
  };

  const handleClearSearch = () => {
    setSearchInputValue("");
    setSearchTerm("");
    setCurrentPage(1);
  };

  const downloadExcel = () => {
    // Create CSV content
    const headers = [
      t("nominationsPage.employeeName"),
      t("nominationsPage.team"),
      t("nominationsPage.seniority"),
      t("nominationsPage.leadershipScore"),
      t("nominationsPage.readinessPercent"),
      t("nominationsPage.potentialRole"),
      t("nominationsPage.strengths"),
      t("nominationsPage.status"),
      t("nominationsPage.nominatedDate"),
    ];
    const csvContent = [
      headers.join(","),
      ...nominations.map((nomination) =>
        [
          nomination.employeeName,
          nomination.employeeTeam,
          nomination.seniority,
          nomination.leadershipScore,
          nomination.readiness,
          nomination.potentialRole,
          `"${nomination.strengths.join(", ")}"`,
          nomination.status,
          nomination.nominatedDate.toLocaleDateString(),
        ].join(","),
      ),
    ].join("\n");

    // Create and download file
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `leadership-nominations-${
      new Date().toISOString().split("T")[0]
    }.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const getScoreColor = (score) => {
    if (score >= 9) return "score-excellent";
    if (score >= 8) return "score-good";
    return "score-average";
  };

  const getReadinessColor = (readiness) => {
    if (readiness >= 90) return "readiness-high";
    if (readiness >= 80) return "readiness-medium";
    return "readiness-low";
  };

  const renderStars = (rating) => {
    const maxRating = 10;
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;
    const emptyStars = maxRating - fullStars - (hasHalfStar ? 1 : 0);

    return (
      <div className="nom-star-container">
        {Array(fullStars)
          .fill(0)
          .map((_, i) => (
            <span key={`full-${i}`} className="nom-star nom-star-full">
              ★
            </span>
          ))}
        {hasHalfStar && <span className="nom-star nom-star-half">★</span>}
        {Array(emptyStars)
          .fill(0)
          .map((_, i) => (
            <span key={`empty-${i}`} className="nom-star nom-star-empty">
              ★
            </span>
          ))}
      </div>
    );
  };

  const confirmRemove = () => {
    if (showModal.itemId && onRemove) {
      onRemove(showModal.itemId);
      setShowModal({
        show: false,
        type: "",
        title: "",
        message: "",
        itemId: null,
        action: null,
        onConfirm: null,
      });
    }
  };

  const getLeadershipNominationsApiHandler = async () => {
    try {
      setLoading(true);
      const response = await getLeadershipNominationsApi(
        getNominationsRequest(),
      );
      const nominationData = Array.isArray(response?.data) ? response.data : [];
      const nextTotalCount = response?.totalCount ?? nominationData.length;

      setNominations(nominationData);
      setTotalCount(nextTotalCount);
      setPeopleLength(nextTotalCount);
    } catch (error) {
      handleApiError(error, {
        showModal: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (nomination) => {
    console.log("Removing nomination:", nomination);
    try {
      const response = await deleteLeadershipNominationApi({
        nominationId: nomination.nominationId,
      });
      console.log("Remove nomination response:", response);

      if (response.code == 200) {
        getLeadershipNominationsApiHandler();
        setShowModal({
          show: true,
          showButton: false,
          type: "success",
          title: t("common.success"),
          message: response.message,
          itemId: null,
          action: null,
          onConfirm: null,
        });
      }
    } catch (error) {
      console.error("Error removing nomination:", error);
      handleApiError(error, {
        showModal: true,
        showButton: false,
      });
    }
  };

  const toggleNominatorExpanded = (nominatorIndex) => {
    setExpandedNominators((prev) => ({
      ...prev,
      [nominatorIndex]: !prev[nominatorIndex],
    }));
  };

  const renderStarRating = (rating) => {
    const maxRating = 10;
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;
    const emptyStars = maxRating - fullStars - (hasHalfStar ? 1 : 0);

    return (
      <div className="np-rating-stars-container">
        <Star size={14} />
      </div>
    );
  };
  const SkeletonCard = () => (
    <div className="nomination-card nomination-card-skeleton">
      <div className="skeleton-header">
        <div className="skeleton-avatar skeleton-shimmer" />
        <div className="skeleton-text-group">
          <div className="skeleton-line skeleton-line-lg skeleton-shimmer" />
          <div className="skeleton-line skeleton-line-sm skeleton-shimmer" />
          <div className="skeleton-line skeleton-line-sm skeleton-shimmer" />
        </div>
      </div>

      <div className="skeleton-badge skeleton-shimmer" />

      <div className="skeleton-progress skeleton-shimmer" />

      <div className="skeleton-line skeleton-line-md skeleton-shimmer" />

      <div className="skeleton-tags">
        <span className="skeleton-tag skeleton-shimmer" />
        <span className="skeleton-tag skeleton-shimmer" />
        <span className="skeleton-tag skeleton-shimmer" />
      </div>
    </div>
  );

  return (
    <div className="nominations-page">
      <BackButton />

      <main className="nominations-main">
        <div className="container">
          <div className="page-header">
            <div className="header-icon">
              <Award size={32} />
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                justifyContent: "center",
              }}
            >
              <h1>{t("nominationsPage.pageTitle")}</h1>
              <span className="nominations-title-info-wrap">
                <button
                  type="button"
                  className="nominations-title-info-btn"
                  aria-label={t("nominationsPage.tooltips.nominated.title")}
                >
                  <Info size={18} />
                </button>

                <span className="nominations-title-info-popover" role="tooltip">
                  <strong>
                    {t("nominationsPage.tooltips.nominated.title")}
                  </strong>
                  <span>{t("nominationsPage.tooltips.nominated.message")}</span>
                </span>
              </span>
            </div>
            <p>{t("nominationsPage.pageDescription")}</p>
          </div>

          {/* <div className="aactions-bar">
            <div className="astats">
              <span className="astat-item">
                {t("nominationsPage.totalNominations")}{" "}
                <strong>{totalCount}</strong>
              </span>
            </div>
          </div> */}

          {/* Smart Search & Filter panel (same as Upskill) */}
          <div className="smart-filter-panel-leadership">
            <div className="smart-filter-top-bar">
              {/* Left side (filters applied) */}
              <div className="smart-filter-left">
                <div className="smart-filter-info">
                  {getAppliedFiltersCount() > 0 && (
                    <>
                      <span className="smart-filter-badge">
                        {getAppliedFiltersCount()}
                      </span>
                      <span>{t("common.filtersApplied")}</span>
                    </>
                  )}
                </div>
              </div>

              {/* Right side (search input + icons pinned right always) */}
              <div className="smart-filter-right">
                {showSearchBar && (
                  <div className="smart-search-inline-row smart-search-right">
                    <input
                      type="text"
                      placeholder={t("leadershipPage.searchPlaceholder")}
                      value={searchInputValue}
                      onChange={(e) => {
                        setSearchInputValue(sanitizeSearch(e.target.value));
                      }}
                      className="smart-search-inline-input"
                      autoFocus
                    />
                    <span className="mobile-search-placeholder-hint">
                      {t("leadershipPage.searchPlaceholder")}
                    </span>

                    {/* close search */}
                    <button
                      className="smart-filter-icon-btn active"
                      title={t("common.search")}
                      onClick={handleSearchButtonClick}
                    >
                      <Search size={18} />
                    </button>

                    {searchTerm && (
                      <button
                        className="smart-search-clear"
                        onClick={handleClearSearch}
                        title={t("common.clear")}
                      >
                        <X size={18} />
                      </button>
                    )}
                  </div>
                )}

                {/* Icons pinned to the right */}
                <div className="smart-filter-icons-right">
                  {!showSearchBar && (
                    <button
                      className={`smart-filter-icon-btn ${showSearchBar ? "active" : ""}`}
                      onClick={() => {
                        setSearchInputValue(searchTerm);
                        setShowSearchBar(true);
                        setShowFiltersPanel(false);
                      }}
                      title={t("common.search")}
                    >
                      <Search size={18} />
                    </button>
                  )}

                  <div className="smart-filter-separator" />

                  <button
                    className={`smart-filter-icon-btn ${showFiltersPanel ? "active" : ""}`}
                    onClick={() => {
                      setShowFiltersPanel(!showFiltersPanel);
                      if (!showFiltersPanel) setShowSearchBar(false);
                    }}
                    title={t("common.filters")}
                  >
                    <Filter size={18} />
                  </button>
                </div>
              </div>
            </div>

            {showFiltersPanel && (
              <>
                <div className="smart-filter-divider" />
                <div className="smart-filter-content">
                  <div className="smart-filter-grid">
                    <div className="smart-filter-item">
                      <label>{t("leadershipPage.filters.seniority")}</label>
                      <select
                        value={filterSeniority}
                        onChange={(e) => {
                          removeCountFromUrl();
                          setFilterSeniority(e.target.value);
                        }}
                        className={`smart-filter-select ${filterSeniority !== "All" ? "smart-active" : ""}`}
                      >
                        {seniorities.map((seniority) => (
                          <option key={seniority.value} value={seniority.value}>
                            {seniority.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="smart-filter-item">
                      <label>{t("leadershipPage.filters.sortBy")}</label>
                      <select
                        value={sortBy}
                        onChange={(e) => {
                          removeCountFromUrl();
                          setSortBy(e.target.value);
                        }}
                        className={`smart-filter-select ${sortBy !== "Leadership Score" ? "smart-active" : ""}`}
                      >
                        {sortOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="smart-filter-item">
                      <label>{t("leadershipPage.filters.readiness")}:</label>
                      <CustomReadinessFilter
                        options={readinessOptions}
                        selectedValues={
                          filterReadiness.length ? filterReadiness : ["All"]
                        }
                        onSelect={handleSelect}
                        onRemove={handleReadinessRemove}
                        label={t("leadershipPage.filters.readiness")}
                        getTooltip={LeadershipreadinessTooltip}
                      />
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          {loading ? (
            // SKELETON VIEW
            <div className="nominations-grid">
              {Array.from({ length: 6 }).map((_, idx) => (
                <SkeletonCard key={idx} />
              ))}
            </div>
          ) : nominations.length === 0 ? (
            <div className="empty-state">
              <Award size={48} />
              <h3>{t("nominationsPage.noNominations")}</h3>
              <p>{t("nominationsPage.visitLeadershipPipeline")}</p>
              <div
                style={{
                  display: "flex",
                  width: "100%",
                  justifyContent: "center",
                }}
              >
                <button
                  className="btn btn-primary"
                  onClick={() => navigate("/talent-development/leadership")}
                >
                  {t("nominationsPage.goToLeadershipPipeline")}
                </button>
              </div>
            </div>
          ) : (
            <div className="nominations-grid">
              {nominations.length &&
                nominations.map((nomination, index) => (
                  <div key={nomination.userId} className="nomination-card">
                    <div className="card-header">
                      <div className="person-info">
                        <div className="person-avatar">
                          <User size={20} />
                        </div>
                        <div>
                          <h3>
                            {nomination?.name ||
                              t("nominationsPage.nameNotFound")}
                          </h3>
                          <p className="team">
                            {nomination?.department ||
                              t("nominationsPage.departmentNotFound")}
                          </p>
                          <p className="seniority">
                            {nomination.level
                              ? nomination.level
                              : t("common.notAvailable")}
                          </p>
                        </div>
                      </div>
                      <div
                        className="card-actions"
                        style={{ marginTop: "0px" }}
                      >
                        <div
                          className={`lleadership-score ${getScoreColor(
                            nomination.rating,
                          )}`}
                        >
                          <Star size={14} />
                          {nomination?.rating || 0}/10
                        </div>
                        <button
                          disabled={
                            !leadershipAccess.canEditLeadershipPotential
                          }
                          className="remove-btn"
                          onClick={() => handleRemove(nomination)}
                          title={t("nominationsPage.removeNomination")}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>

                    <div className="potential-role">
                      <div
                        className="role-badge"
                        onClick={() => {
                          setShowNominatorList({
                            show: true,
                            title: t("nominationsPage.nominatedBy"),
                            nominators: Array.isArray(nomination?.nominators)
                              ? nomination.nominators
                              : [],
                            rating: nomination?.rating || 0,
                          });
                        }}
                      >
                        <span>
                          {t("nominationsPage.nominatedFor")}{" "}
                          {nomination?.nominationCount || 0}
                        </span>
                      </div>
                    </div>

                    <div className="readiness-compact">
                      <div className="semicircle-progress">
                        <div
                          className="semicircle-container"
                          style={{ top: "80px", right: "30px" }}
                        >
                          <CircularProgressbar
                            value={nomination.leadershipPotentialScore / 2}
                            maxValue={100}
                            text={""}
                            styles={buildStyles({
                              pathColor: "#672024",
                              trailColor: "#e0e0e0",
                              strokeLinecap: "butt",
                              rotation: 0.75,
                            })}
                          />
                        </div>
                        <div
                          style={{
                            position: "absolute",
                            top: "130px",
                            right:
                              nomination.leadershipPotentialScore === 100
                                ? "50px"
                                : "60px",
                            transform: "translate(-50%, -50%)",
                            fontSize: "19px",
                            fontWeight: "500",
                            color: "#333",
                          }}
                        >
                          {nomination?.leadershipPotentialScore || 0}%
                        </div>
                        <div
                          className="readiness-info-below"
                          style={{ marginTop: "-20px", marginRight: "35px" }}
                        >
                          <TrendingUp size={14} />
                          <span>{t("leadershipPage.readiness")}</span>
                        </div>
                      </div>
                    </div>

                    <div className="nomination-details">
                      <div className="detail-item">
                        <Calendar size={16} />
                        <span>
                          {t("nominationsPage.nominated")}
                          {new Date(nomination?.updatedAt).toLocaleDateString()}
                        </span>
                      </div>
                    </div>

                    <div className="strengths-section">
                      <h4>{t("nominationsPage.leadershipStrengths")}</h4>
                      <div className="strengths-tags" aria-live="polite">
                        {(() => {
                          const strengths = (
                            nomination?.leadershipStrengths ?? []
                          ).filter(
                            (s) => typeof s === "string" && s.trim().length > 0,
                          );

                          return strengths.length > 0 ? (
                            strengths.map((strength, i) => (
                              <span
                                key={`${strength}-${i}`}
                                className="strength-tag"
                              >
                                {strength}
                              </span>
                            ))
                          ) : (
                            <span
                              style={{
                                fontSize: "12px",
                                fontStyle: "italic",
                                color: "#777",
                              }}
                            >
                              {t("nominationsPage.noStrengthsIdentified")}
                            </span>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          )}

          {totalCount > 0 && (
            <Pagination
              // loading={false}
              loading={loading}
              total={totalCount}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              currentPageCount={nominations.length}
              onPageChange={setCurrentPage}
              pageSizeOptions={[10, 20, 40, 100]}
              onPageSizeChange={(size) => {
                setItemsPerPage(size);
                const newTotalPages = Math.max(
                  1,
                  Math.ceil((totalCount || 0) / size),
                );
                if (currentPage > newTotalPages) setCurrentPage(newTotalPages);
              }}
              infoRenderer={({ start, end, total }) =>
                t("nominationsPage.paginationInfo", {
                  start,
                  end,
                  total,
                })
              }
            />
          )}
        </div>
      </main>
      {showNominatorList.show && (
        <Modal
          show
          type="info"
          title={showNominatorList.title}
          onClose={() =>
            setShowNominatorList({
              show: false,
              title: "",
              nominators: [],
              rating: 0,
            })
          }
          showButton={false}
        >
          <ul className="np-nominator-list">
            {showNominatorList.nominators.length === 0 ? (
              <li
                className="np-nominator-item"
                style={{ fontStyle: "italic", color: "#777" }}
              >
                {t("nominationsPage.noNominatorsFound")}
              </li>
            ) : (
              showNominatorList.nominators.map((person, index) => (
                <li key={index} className="np-nominator-wrapper">
                  <div className="np-nominator-item">
                    <div className="np-nominator-header">
                      <div className="np-nominator-info">
                        <span className="np-nominator-name">
                          {person.createdBy}
                          {/* {person.count ? ` (x${person.count})` : ""} */}
                        </span>
                      </div>
                      <div className="np-nominator-actions">
                        <div
                          className={`leadership-score ${getScoreColor(
                            person.rating,
                          )}`}
                        >
                          <Star size={14} /> {person?.ratings || 0}/10
                        </div>
                        <button
                          className="np-comments-toggle-btn"
                          onClick={() => toggleNominatorExpanded(index)}
                          title={
                            expandedNominators[index]
                              ? t("nominationsPage.hideComments")
                              : t("nominationsPage.showComments")
                          }
                        >
                          <MessageCircle size={16} />
                          <ChevronDown
                            size={16}
                            className={`np-chevron-icon ${
                              expandedNominators[index]
                                ? "np-chevron-expanded"
                                : ""
                            }`}
                          />
                        </button>
                      </div>
                    </div>

                    {expandedNominators[index] && (
                      <div className="np-comments-section">
                        <div className="np-comments-container">
                          {(() => {
                            const comments = Array.isArray(person?.comments)
                              ? person.comments.filter(
                                  (comment) =>
                                    typeof comment === "string" &&
                                    comment.trim().length > 0,
                                )
                              : typeof person?.comments === "string" &&
                                  person.comments.trim().length > 0
                                ? [person.comments]
                                : [];

                            return comments.length > 0 ? (
                              comments.map((comment, commentIndex) => (
                                <div
                                  key={`${person.createdBy}-${commentIndex}`}
                                  className="np-comment-item"
                                >
                                  <p className="np-comment-text">
                                    {comment.trim()}
                                  </p>
                                </div>
                              ))
                            ) : (
                              <div className="np-no-comments">
                                <span>
                                  {t("nominationsPage.noCommentsAdded")}
                                </span>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    )}
                  </div>
                </li>
              ))
            )}
          </ul>
        </Modal>
      )}

      <Modal
        show={showModal.show}
        onClose={() =>
          setShowModal({
            show: false,
            type: "",
            title: "",
            message: "",
            action: null,
            onConfirm: null,
          })
        }
        type={showModal.type}
        title={showModal.title}
        message={showModal.message}
        showButton={!!showModal.action}
        buttonText={t("common.confirm")}
        onButtonClick={showModal.action}
        onConfirm={showModal.onConfirm}
      />

      <SubscriptionExpiredModal
        show={showSubscriptionExpired}
        message={subscriptionMessage}
        onRedirect={handleSubscriptionRedirect}
      />
    </div>
  );
};

export default NominationsPage;
